<template>
    <el-drawer title="Certifications" v-model="localCertificationDialogVisible" :append-to-body="true"
        :direction="'btt'" :destroy-on-close="true">
        <el-form :model="editCertificateForm" ref="editForm">
            <el-form-item class="cert-item">
                <div v-for="(cer, index) in certifications" :key="index" class="certification-input text-left">
                    <el-input v-model="cer.name" type="text" placeholder="Name" @blur="cer.dirty.name = true"
                        maxlength="150"
                        show-word-limit
                        :error="$v.certifications.$each.$response.$data[index].name.$error"
                        :class="{ 'error':cer.dirty.name && $v.certifications.$each.$response.$data[index].name.$error }"></el-input>
                    <el-form-item-error v-if="cer.dirty.name && $v.certifications.$each.$response.$data[index].name.$error">
                        {{ $v.certifications.$each.$response.$errors[index].name[0].$message }}
                    </el-form-item-error>
                    <el-button type="danger" @click="removeCertification(index)">
                        <TrashIcon />
                    </el-button>
                </div>
                <el-button class="add-certification-btn" type="primary" @click="addCertification">Add</el-button>
            </el-form-item>
        </el-form>
        <div slot="footer" class="d-flex gap-1 my-3" >
            <el-button class="cert-save-btn mt-0" type="primary" @click="saveCertificates"
                :disabled="isSaveButtonDisabled">Save</el-button>
            <el-button class="secondary mt-0" @click="closeCertificationDialog">Cancel</el-button>
        </div>
    </el-drawer>
</template>

<script setup>

import { ref, reactive, defineProps, defineEmits, watch, toRefs, computed, onBeforeMount } from 'vue'
import { useStore } from 'vuex'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useVuelidate } from '@vuelidate/core'
import { helpers } from '@vuelidate/validators'
import TrashIcon from '@/components/Icons/TrashIcon.vue'

// Store and Props
const store = useStore();
const userData = ref(store.getters.GET_USER_DATA ? store.getters.GET_USER_DATA : {});
const userRole = userData.value.role;
const userCertifications = ref(
    userData.value.settings?.hrFlowData?.certifications ||
    userData.value.locumAsJobSeekerSetting?.hrFlowData?.certifications ||
    []
);

const props = defineProps({
    certificationDialogVisible: {
        type: Boolean,
        required: false
    }
});

// Emit event
const emit = defineEmits(['closeCertificationDrawer']);

// Reactive Form Data
const editCertificationForm = reactive({
    certifications: []
});

// Break the reactivity chain to avoid constructor error
const { certifications } = toRefs(editCertificationForm);

// Fetch user data when drawer is opened, added this to fix the issue of not updating the certifications list after adding a new experience
const fetchUserData = async () => {
    try {
        userData.value = store.getters.GET_USER_DATA ? store.getters.GET_USER_DATA : {};
        userCertifications.value = userData.value.settings?.hrFlowData?.certifications || userData.value.locumAsJobSeekerSetting?.hrFlowData?.certifications || [];
        editCertificationForm.certifications = userCertifications.value.map(exp => ({ ...exp, dirty: { name:false } }));
    } catch (error) {
        ElMessage({ type: 'error', message: error.message });
    }
};

// Validation rules
const rules = computed(() => {
    return {
        certifications: {
            $each: helpers.forEach({
                name: {
                    required: helpers.withMessage('Name is required', value => value.trim() !== '')
                },
            })
        }
    };
});

// Initialize Vuelidate
const $v = useVuelidate(rules, editCertificationForm);

// Watchers to Sync Drawer Visibility
const localCertificationDialogVisible = ref(props.certificationDialogVisible);

watch(localCertificationDialogVisible, (newVal) => {
    if (!newVal) closeCertificationDialog();
});

watch(() => props.certificationDialogVisible, (newVal) => {
    localCertificationDialogVisible.value = newVal;
    // if newVal is true, get updated user data and set certifications
    if (newVal) {
        fetchUserData();
    }
});

onBeforeMount(() => {
    fetchUserData();
});

// Functions
const addCertification = () => {
    certifications.value.push({ name: '', dirty: { name:false } });
};

const removeCertification = (index) => {
    certifications.value.splice(index, 1);
};

const isSaveButtonDisabled = computed(() => {
    return $v.value.$pending || $v.value.$error || $v.value.$invalid;
});

// OLD Data
const saveCertificates = async () => {

    const isValid = await $v.value.$validate();

    if (!isValid) {
        ElMessage({ type: 'error', message: 'Please fill out all required fields.' });
        return;
    }

    try {
        await ElMessageBox.confirm(
            `Are you sure you want to update certifications?`,
            "Update Certifications",
            {
                confirmButtonText: "Update",
                cancelButtonText: "Cancel",
                confirmButtonClass: "el-button--primary",
                cancelButtonClass: "el-button--grey",
                showClose: false,
            }
        );

        await (async () => {
            try {
                let settingId = null;
                if(userRole === 'locum') {
                    settingId = userData.value.locumAsJobSeekerSetting.id;
                } else {
                    settingId = userData.value.settings.id;
                }
                const response = await store.dispatch("UPDATE_JOB_SEEKER_CERTIFICATES", { params: editCertificationForm, id: settingId });

                store.dispatch("SET_CURRENT_USER_DATA", {
                    with:
                        "settings,billingInfo,schedule,bankVerification,address,push_notifications,config.hours_before_shift_lock",
                });
                if (response.status) {
                    ElMessage({
                        type: "success",
                        message: response.message,
                    });

                } else {
                    ElMessage({
                        type: "info",
                        message: response.message,
                    });
                }
            } catch (error) {
                if (error.message) {
                    ElMessage({ type: 'error', message: error.message });
                }
            }
        })();
        closeCertificationDialog();
    } catch (error) {
        console.log(error);
    }
}
const closeCertificationDialog = () => {
    certifications.value = [];
    emit('closeCertificationDrawer', false);
};
</script>
<style lang="scss">
.el-overlay.is-message-box,
.el-message.el-message--error {
    z-index: 9999 !important;
}
</style>